// The new search's calls to Loggione (F07), as TanStack Query hooks: one
// search paged by offset, and Le mie ricerche with its three changes. The
// components never see the api: the page hands the answers to adapt.ts.
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, isApiError, unwrap } from "../../api/client";
import { keys } from "../../api/keys";
import type { components } from "../../api/schema";
import type { SavedSearch, SavedSearches } from "./adapt";
import { searchKey, type SearchQuery } from "./params";

export type NewSavedSearch = components["schemas"]["NewSavedSearch"];

/** Hits per page: «Carica altri» asks the next 20. The api serves the first 200. */
export const SEARCH_PAGE_SIZE = 20;

/**
 * One search, asked when it has words (`enabled`). A 404 means the switch was
 * turned off for the caller while the page was open: /me is asked again, so
 * the whole page goes back to the page of today. Never retried on a 4xx (the
 * app's default): a 422 is the answer.
 */
export function useSearch(query: SearchQuery, enabled: boolean) {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: keys.search(searchKey(query)),
    queryFn: async ({ pageParam }) => {
      try {
        return unwrap(
          (
            await api().GET("/api/v1/search/v2", {
              params: { query: { ...query, limit: SEARCH_PAGE_SIZE, offset: pageParam } },
            })
          ).data,
        );
      } catch (error) {
        if (isApiError(error) && error.status === 404) void queryClient.invalidateQueries({ queryKey: keys.me });
        throw error;
      }
    },
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset,
    enabled,
  });
}

/** GET /search/forms: «Plurale (Legge, Leggi)» of a word, asked when its menu opens. */
export function usePluralForms(word: string, enabled: boolean) {
  return useQuery({
    queryKey: ["search", "forms", word.toLowerCase()],
    queryFn: async () => unwrap((await api().GET("/api/v1/search/forms", { params: { query: { word } } })).data),
    enabled,
    staleTime: Infinity,
  });
}

/** GET /saved-searches: Le mie ricerche, the newest first. */
export function useSavedSearches(enabled = true) {
  return useQuery({
    queryKey: keys.savedSearches,
    queryFn: async () => unwrap((await api().GET("/api/v1/saved-searches")).data),
    enabled,
  });
}

/** POST /saved-searches (201): waits for the api (a name taken, the 50th); then the list takes it and is asked again. */
export function useCreateSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: NewSavedSearch) => unwrap((await api().POST("/api/v1/saved-searches", { body })).data),
    onSuccess: (created) => {
      queryClient.setQueryData<SavedSearches>(keys.savedSearches, (old) =>
        old ? { ...old, items: [created, ...old.items.filter((s) => s.id !== created.id)] } : old,
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.savedSearches });
    },
  });
}

type SavedContext = { previous: SavedSearch | undefined };

/** What PATCH /saved-searches/{id} changes: the name, the folder (null: out of its folder), or both. */
export type SavedSearchChange = { id: string; name?: string; folder?: string | null };

/**
 * PATCH /saved-searches/{id}: the new name or folder shows at once and goes
 * back if the api refuses it; on settle the list is asked again (the api
 * writes a folder as the one already there: energia → Energia).
 */
export function useChangeSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...change }: SavedSearchChange) =>
      unwrap(
        (
          await api().PATCH("/api/v1/saved-searches/{saved_search_id}", {
            params: { path: { saved_search_id: id } },
            body: change,
          })
        ).data,
      ),
    onMutate: async ({ id, ...change }): Promise<SavedContext> => {
      await queryClient.cancelQueries({ queryKey: keys.savedSearches });
      const previous = queryClient.getQueryData<SavedSearches>(keys.savedSearches)?.items.find((s) => s.id === id);
      queryClient.setQueryData<SavedSearches>(keys.savedSearches, (old) =>
        old ? { ...old, items: old.items.map((s) => (s.id === id ? { ...s, ...change } : s)) } : old,
      );
      return { previous };
    },
    onError: (_error, { id }, context) => {
      const previous = context?.previous;
      if (!previous) return;
      queryClient.setQueryData<SavedSearches>(keys.savedSearches, (old) =>
        old ? { ...old, items: old.items.map((s) => (s.id === id ? previous : s)) } : old,
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.savedSearches });
    },
  });
}

/**
 * DELETE /saved-searches/{id} (204): the search leaves the list at once and
 * comes back, in its place, if the api refuses.
 */
export function useDeleteSavedSearch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api().DELETE("/api/v1/saved-searches/{saved_search_id}", {
        params: { path: { saved_search_id: id } },
      });
    },
    onMutate: async (id): Promise<SavedContext & { index: number }> => {
      await queryClient.cancelQueries({ queryKey: keys.savedSearches });
      const items = queryClient.getQueryData<SavedSearches>(keys.savedSearches)?.items ?? [];
      const index = items.findIndex((s) => s.id === id);
      queryClient.setQueryData<SavedSearches>(keys.savedSearches, (old) =>
        old ? { ...old, items: old.items.filter((s) => s.id !== id) } : old,
      );
      return { previous: items[index], index };
    },
    onError: (error, id, context) => {
      const previous = context?.previous;
      // a 404: it is gone already, it does not come back
      if (!previous || (isApiError(error) && error.status === 404)) return;
      queryClient.setQueryData<SavedSearches>(keys.savedSearches, (old) => {
        if (!old || old.items.some((s) => s.id === id)) return old;
        const items = [...old.items];
        items.splice(Math.min(context.index, items.length), 0, previous);
        return { ...old, items };
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.savedSearches });
    },
  });
}
